import * as ModalTypes from '@components/App/Modal/constants'
import type { Project, RelationsStore } from '@octopus-do/octopus-project-core'
import { LoaderActions } from '@store/loaders'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { DashboardActions } from '@store/reducers/dashboard/actions'
import { LimitReachedActions } from '@store/reducers/limitReached'
import { managerActions } from '@store/reducers/manager/actions'
import { metaActions } from '@store/reducers/manager/meta/actions'
import type { AppState } from '@store/types'
import * as ActionTypes from '@storeOld/actions'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { closeModal } from '@storeOld/reducers/ui'
import axios, { type AxiosResponse } from 'axios'
import { push, type RouterState, replace } from 'redux-first-history'
import { call, put, select } from 'redux-saga/effects'

import API from '@/api'
import { ProjectOriginEvents, sendGtmEvent } from '@/constants/gtm'
import { ProjectOrigin } from '@/types'

type SaveProjectRequestPayload = {
  project: Project
  relations: RelationsStore
  origin: ProjectOrigin
}

export const checkCanSaveProject = (
  token?: string
): Promise<AxiosResponse<any>> => {
  const authToken = token ?? localStorage.getItem('token')

  return axios.get(`/my/can-save-project`, {
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'X-Auth-Token': authToken,
    },
  })
}

const saveTeamProjectRequest = (
  teamId: string,
  payload: SaveProjectRequestPayload
): Promise<AxiosResponse<any>> => {
  const token = localStorage.getItem('token')

  return axios.post(`/team/${teamId}`, payload, {
    baseURL: import.meta.env.VITE_SYNC_HTTP_URL,
    headers: {
      'X-Auth-Token': token,
    },
  })
}

const saveProjectRequest = (
  payload: SaveProjectRequestPayload,
  { anonymous = false }: { anonymous?: boolean } = {}
): Promise<AxiosResponse<any>> => {
  // Without a token the project is created ownerless — a trial project.
  const token = anonymous ? null : localStorage.getItem('token')

  return axios.post(`/projects`, payload, {
    baseURL: import.meta.env.VITE_SYNC_HTTP_URL,
    headers: {
      'X-Auth-Token': token,
    },
  })
}

export function* saveProjectSaga(action: any): Generator<any, void, any> {
  const ignoreAuthenticated = action.payload?.ignoreAuthenticated
  const popupDownloadSitemapXml = action.payload?.popupDownloadSitemapXml
  const teamId = action.payload?.teamId
  const origin = action.payload?.origin ?? ProjectOrigin.ORDINARY
  const fallbackToTrialOnLimit = action.payload?.fallbackToTrialOnLimit

  // Set when the limit is reached but the flow asked to keep going: the project
  // is created without an owner, so it must not be counted as an owned create.
  let createAsTrial = false

  // For the "project saved under an account" GTM event: prefer the origin the
  // creating flow passed; otherwise (e.g. claiming a trial project from the
  // header badge) fall back to the origin the project was loaded with.
  const storeOrigin: ProjectOrigin = yield select(
    migrationProjectSelector.getProjectOrigin
  )
  const gtmOrigin: ProjectOrigin = action.payload?.origin ?? storeOrigin

  try {
    const projectOwnerId = yield select(
      migrationProjectSelector.getProjectOwnerId
    )

    // ignore saving project if project already has owner
    if (projectOwnerId) {
      return
    }

    yield put(LoaderActions.set('project_save'))

    const { authenticated } = yield select((state: AppState) => state.auth)
    let projectId = yield select(migrationProjectSelector.getProjectId)

    if ((authenticated || localStorage.getItem('token')) && !teamId) {
      const responseCanSave = yield call(checkCanSaveProject)
      if (responseCanSave.status !== 200) {
        throw new Error(
          `Request of checking if can save project failed with status ${responseCanSave.status}`
        )
      }

      if (!responseCanSave.data.canSave) {
        if (!fallbackToTrialOnLimit) {
          // The flow that asked to save may still have its modal up (the
          // crawler's progress, the import tabs). Nothing else closes it on a
          // refusal, so do it here before the upsell takes the screen.
          yield put(closeModal())
          yield put(LimitReachedActions.alert())

          return
        }

        createAsTrial = true
      }
    }

    if (projectId) {
      if (!ignoreAuthenticated && !authenticated) {
        yield put(LoaderActions.unset('project_save'))

        yield put({
          type: ActionTypes.OPEN_MODAL,
          payload: {
            modalType: ModalTypes.MODAL_AUTH,
            modalProps: {
              redirectAfterAuth: `/${projectId}?save`,
            },
          },
        })

        return
      }

      const saveProjectResponse = yield call(() =>
        API.Projects.save({
          id: projectId,
        })
      )

      if (saveProjectResponse) {
        // Project just moved from trial to owned by this user — count it.
        if (authenticated) {
          sendGtmEvent(ProjectOriginEvents[gtmOrigin])
        }

        // The project is already on screen — pull the new meta (owner, access)
        // in place instead of a full page reload.
        yield put(
          managerActions.fetchProjectSaga({ id: projectId, silent: true })
        )

        const location: RouterState['location'] = yield select(
          (state: AppState) => state.router.location
        )
        const searchParams = new URLSearchParams(location?.search ?? '')

        if (location && searchParams.has('save')) {
          searchParams.delete('save')

          const cleanedSearch = searchParams.toString()

          yield put(
            replace(
              `${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}`
            )
          )
        }
      }

      return
    }

    const currentState = yield select((state) => ({
      project: state.manager.project,
      relations: state.relations,
    }))

    currentState.origin = origin

    const response: AxiosResponse<any> = teamId
      ? yield call(saveTeamProjectRequest, teamId, currentState)
      : yield call(saveProjectRequest, currentState, {
          anonymous: createAsTrial,
        })

    if (response.status !== 200) {
      throw new Error(
        `Request of saving project failed with status ${response.status}`
      )
    }

    const { data } = response

    projectId = data.id

    yield put({
      type: metaActions.updateMeta.type,
      payload: data,
    })

    yield put(closeModal())

    const search = popupDownloadSitemapXml
      ? `?${new URLSearchParams({ popupDownloadSitemapXml: 'true' })}`
      : ''
    yield put(push(`/${projectId}${search}`))

    // Limit was reached, so the project was created ownerless. Show the upsell
    // now that we are on the project route, which renders modals/notifications.
    if (createAsTrial) {
      yield put(LimitReachedActions.alert())

      return
    }

    // Optimistically put the new project into the dashboard store, so the
    // list renders complete on return instead of the project jumping in
    // after a refetch. Anonymous creates stay trial — not on the dashboard.
    if (authenticated) {
      yield put(
        DashboardActions.projects.created({
          id: projectId,
          teamId: teamId ?? null,
          title: currentState.project.settings.title,
          theme: currentState.project.settings.theme,
        })
      )
    }

    // Authenticated create = project owned immediately (not a trial) — count it.
    // Anonymous creates stay trial and are counted later, on claim above.
    if (authenticated) {
      sendGtmEvent(ProjectOriginEvents[gtmOrigin])
    }

    if (!ignoreAuthenticated && !authenticated) {
      yield put(LoaderActions.unset('project_save'))

      yield put({
        type: ActionTypes.OPEN_MODAL,
        payload: {
          modalType: ModalTypes.MODAL_AUTH,
          modalProps: {
            redirectAfterAuth: `/${projectId}?save`,
          },
        },
      })
    }
  } catch (error) {
    console.error('Error saving project:', error)

    yield put(
      notificationActionCreators.setNotification({
        type: NotificationType.PROBLEM_WITH_SAVING_CHANGES,
      })
    )
  } finally {
    yield put(LoaderActions.unset('project_save'))
  }
}
